import React, { useEffect, useState } from "react";
import botImage from 'assets/images/svg/zerone-bot-3-d-2.svg'
import styles from 'styles/components/ProfilePictureWrapper.module.sass'
import ReusableIcon from "./ReusableIcon";
import endpoints from "constants/api/Endpoints";
import { logError } from "services/logService/errorLogger";
import { useToast } from "provider/ToastProvider";
import { v4 as uuidv4 } from "uuid";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import { getStorage, ref, uploadBytes } from "firebase/storage";
import { initializeApp } from "firebase/app";
import { firebaseConfig } from "constants/data/global/firebaseConfig";
import ZeroneLoader from "./ZeroneLoader";

// Initialize Firebase app
const app = initializeApp(firebaseConfig);

const tag = 'ProfilePictureWrapper'

// Props for the ProfilePictureWrapper component.
interface ProfilePictureWrapperProps {
    profilePicture: string;
}

/**
 * ProfilePictureWrapper component displays the profile picture of the user.
 *
 * @component
 * @param {ProfilePictureWrapperProps} props - The properties object.
 * @param {string} props.profilePicture - The URL of the profile picture.
 *
 * @example
 * <ProfilePictureWrapper
 *   profilePicture='https://example.com/profile.jpg'
 * />
 */
const ProfilePictureWrapper: React.FC<ProfilePictureWrapperProps> = ({ profilePicture }) => {
    const [avatar, setAvatar] = useState<string>(profilePicture.length > 0 ? profilePicture : botImage);
    const [uploadToken, setUploadToken] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const { addToast } = useToast();

    /**
     * Retrieves an upload token from the server.
     * 
     * @returns {Promise<boolean>} A promise that resolves to a boolean value.
     * 
     * @throws {Error} Throws an error if the fetch operation fails.
     */
    const getUploadToken = async (): Promise<boolean> => {
        try {
            const tokenResult = await endpoints.fetchUploadToken({ tag });
            if (tokenResult && tokenResult.success && tokenResult.data) {
                setUploadToken(tokenResult.data.token);
                return true;
            } else {
                logError('Failed to fetch upload token', { error: 'Token' }, tag);
                return false;
            }
        } catch (error) {
            logError('Error fetching upload token', { error }, tag);
            return false;
        }
    }

    useEffect(() => {
        getUploadToken();
    }, []);

    useEffect(() => {
        if (profilePicture && profilePicture.length > 0) {
            setAvatar(profilePicture);
        } else {
            setAvatar(botImage);
        }
    }, [profilePicture]);

    /**
     * Selects a photo from the user's device.
     *
     * @returns {Promise<File | null>} The selected File, or null if cancelled/invalid.
     */
    const selectPhoto = (): Promise<File | null> => {
        return new Promise((resolve) => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*';
            input.click();

            input.addEventListener('change', (event) => {
                const target = event.target as HTMLInputElement;
                const file = target.files && target.files[0];
                if (file) {
                    if (!file.type.startsWith('image/')) {
                        addToast({
                            type: 'error',
                            message: 'Selected file is not an image. Please select a valid image file.'
                        });
                        resolve(null);
                        return;
                    }

                    // Check if the file size is less than 5MB
                    if (file.size > 5 * 1024 * 1024) {
                        addToast({
                            type: 'error',
                            message: 'Selected file is too large. Please select a file smaller than 5MB.'
                        });
                        resolve(null);
                        return;
                    }

                    const reader = new FileReader();
                    reader.onload = (e) => {
                        const result = e.target?.result;
                        if (result) {
                            setAvatar(result as string);
                        }
                        resolve(file);
                    };
                    reader.readAsDataURL(file);
                } else {
                    resolve(null);
                }
            });
        });
    }

    /**
     * Uploads the selected image to Firebase storage.
     *
     * Accepts file and id directly to avoid reading stale React state from
     * the closure — setState calls in selectPhoto are async and wouldn't be
     * reflected yet when this is called immediately after.
     */
    const uploadImage = async (file: File, id: string): Promise<boolean> => {
        if (!app || !uploadToken || !file) {
            logError('Firebase app not initialized or missing upload token or image file.', {}, tag);
            return false;
        }

        try {
            const imageFileCopy = new File([file], `${id}.${file.name.split('.').pop()}`, { type: file.type });
            const imageFileName = imageFileCopy.name;

            const auth = getAuth(app);
            await signInWithCustomToken(auth, uploadToken);

            const storage = getStorage(app);
            const storageRef = ref(storage, `zerone-profiles-uploads/${imageFileName}`);

            await uploadBytes(storageRef, file as Blob);

            // No read-back from storage: the avatar already shows the local preview set in selectPhoto, and the
            // server returns a freshly signed URL the next time the profile is loaded.
            return true;
        } catch (error) {
            logError('Error uploading image', { error }, tag);
            return false;
        }
    }

    /**
     * Updates the profile photo on the server.
     */
    const updateServerSide = async (id: string): Promise<boolean> => {
        try {
            const result = await endpoints.updateUserProfile({ tag, data: { profilePhoto: id }});
            if (result) {
                return true;
            } else {
                logError('Failed to update profile photo', { error: 'Update' }, tag);
                return false;
            }
        } catch (error) {
            logError('Error updating profile photo', { error }, tag);
            return false;
        }
    }

    /**
     * Handles the change photo event.
     *
     * @returns {Promise<void>} A promise that resolves to void.
     */
    const handleChangePhoto = async (): Promise<void> => {
        try {
            if (!uploadToken || uploadToken.length < 1) {
                logError('Upload token not available', { error: 'Token' }, tag);
                addToast({
                    type: 'error',
                    message: 'Failed to upload photo. Please try again later.'
                });
                return;
            }

            // selectPhoto now returns the File directly so we don't rely on
            // React state (setImageFile is async — the state value would still
            // be null when uploadImage ran immediately after).
            const file = await selectPhoto();
            if (!file) return; // user cancelled or validation failed

            setLoading(true);

            const id = uuidv4();
            const firebaseUploadResult = await uploadImage(file, id);

            if (firebaseUploadResult) {
                const serverUpdateResult = await updateServerSide(id);
                if (serverUpdateResult) {
                    addToast({
                        type: 'success',
                        message: 'Photo uploaded successfully.'
                    });
                    window.dispatchEvent(new Event('authChange'));
                    return;
                }
            }

            addToast({
                type: 'error',
                message: 'Failed to upload photo. Please try again later.'
            });
        } catch (error) {
            logError('Error uploading photo', { error }, tag);
            addToast({
                type: 'error',
                message: 'Failed to upload photo. Please try again later.'
            });
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className={styles['profile-picture-wrapper']}>
            <img src={avatar.length > 0 ? avatar : botImage} alt='Profile Picture' className={styles['profile-picture']} />
            {loading && 
                <div className={styles['loader-overlay']}>
                    <ZeroneLoader text=""/>
                </div>
            }
            <div className={styles['camera-overlay']} onClick={() => { handleChangePhoto(); }}>
                <ReusableIcon icon='mdi:camera' className={styles['camera-icon'] || 'camera-icon'} color='#fff' />
                <p className={styles['camera-text']}>Change photo</p>
            </div>
        </div>
    )
}

export default ProfilePictureWrapper;